'use strict'
import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { shallowEqual, useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import { Link } from '@mui/material'
import { SpinnerButton, CopyButton } from '../../common/CustomButton'
import { ChrofyCustomYearSelect } from '../../common/ChrofyCustomSelect'
import { errorLog } from '../../../common/logger/Logger'
import { getMessage } from '../../../common/utils/MessageUtils'
import { getFileNameList } from '../../../common/utils/FileUtils'
import { useModifyClassList as modifyClassList } from '../../../common/utils/CSSUtils'
import {
  viewId,
  titleName,
  orderBy,
  dataFixStatus as dataFixStatusConst,
  masterClassification
} from '../../../_constants/Code'
import { PathInfoList } from '../../../_constants/PathInfoList'
import { useModal, useModalExtra } from '../../../_components/Modal'
import Forbidden from '../../../_components/Forbidden'
import RelatedDashboard from '../parts/RelatedDashboard'

import './extra/types'
import { hasErrors, keysToSnakeCase } from './extra/common'
import {
  fetchMaster,
  fetchtDataCreateTask,
  fetchEmploymentTypesInfo,
  fetchEmploymentTypeSetting,
  fetchPrescribedWorkingHoursMonth,
  fetchPrescribedWorkingHoursYear,
  fetchEmployeeCountSetting,
  updateEmployeeCount
} from './extra/services'
import {
  parseEmploymentTypeCsv,
  parseEmploymentTypeSettingCsv,
  parsePrescribedWorkingHoursMonthCsv,
  parsePrescribedWorkingHoursYearCsv,
  parseEmployeeCountSetting,
  filterDataCreateTask,
  parseStandardFormatSecondment
} from './extra/dataParsers'
import { mergeErrors } from './extra/helpers'
import { getCurrentFiscalYear, fetchFiscalYearDateList, getFiscalYear } from './extra/fiscalYearUtils'
import { EmployeeCountInfoProviderStore, useEmployeeCountDispatch, useEmployeeCountSelector } from './extra/store'
import {
  employeeCountSettingFields,
  employmentTypeSettingFields,
  prescribedWorkingHoursMonthFields,
  prescribedWorkingHoursYearFields,
  validateRequiredFields,
  validateEmploymentTypeSetting
} from './extra/validations'

import RoundingMethodSetting from './components/RoundingMethodSetting'
import EmploymentTypeSetting from './components/EmploymentTypeSetting'
import PrescribedWorkingHoursMonthSetting from './components/PrescribedWorkingHoursMonthSetting'
import PrescribedWorkingHoursYearSetting from './components/PrescribedWorkingHoursYearSetting'
import EmployeeOnLeaveTargetSetting from './components/EmployeeOnLeaveTargetSetting'
import IndividualSecondmentSetting from './components/IndividualSecondmentSetting'
import ConcurrentSecondmentTargetSetting from './components/ConcurrentSecondmentTargetSetting'
import { ShowServerErrors } from './components/ShowErrors'

/**
 * EmployeeCountInfoContent コンポーネント
 *
 * 人数カウント設定画面の表示コンテンツを担当するコンポーネント
 *
 * @param {Object} props - コンポーネントのプロパティ
 * @param {string} props.pageTitle - ページのタイトル
 * @param {string} props.corporationNumber - 法人番号
 * @param {string} props.corporationName - 企業名
 * @param {string} props.accPeriod - 決算月
 * @param {string} props.userId - ユーザーID
 * @param {string} props.userName - ユーザー名
 * @param {string} props.bucketName - バケット名（環境変数と法人番号から生成）
 * @param {function} props.navigate - 画面遷移用関数
 * @returns {React.ReactElement}
 * @component
 */
const EmployeeCountInfoContent = (props) => {
  const dispatch = useEmployeeCountDispatch()
  const [isLoaded, setIsLoaded] = React.useState(false)
  const [master, setMaster] = React.useState([])

  /*==================================
    画面状態
  ===================================*/
  const { selectedFiscalYear, fiscalYearList, standardFormatSecondmentList, fiscalYearSettings, dataFixStatus } =
    useEmployeeCountSelector((state) => {
      const selectedFiscalYear = state.employeeCountInfo.selectedFiscalYear ?? ''
      const fiscalYearList = state.employeeCountInfo.fiscalYearList ?? []
      const standardFormatSecondmentList = state.employeeCountInfo.standardFormatSecondmentList ?? []
      const fiscalYearSettings = state.employeeCountInfo.fiscalYearSettings[selectedFiscalYear]

      return {
        selectedFiscalYear,
        fiscalYearList,
        standardFormatSecondmentList,
        fiscalYearSettings,
        dataFixStatus: fiscalYearSettings?.dataFixStatus ?? ''
      }
    }, shallowEqual)

  // エラー情報
  const fiscalYearErrors = React.useMemo(() => fiscalYearSettings?.errors?.client ?? null, [fiscalYearSettings])
  const serverErrors = React.useMemo(() => fiscalYearSettings?.errors?.server ?? null, [fiscalYearSettings])

  // 年度選択リスト定義
  const options = useMemo(() => {
    return fiscalYearList.map((fiscalYear) => ({
      label: getFiscalYear(fiscalYear, props.accPeriod),
      value: fiscalYear
    }))
  }, [fiscalYearList, props.accPeriod])

  // 最古年度取得
  const minFiscalYear = useMemo(() => {
    if (!Array.isArray(fiscalYearList) || fiscalYearList.length === 0) return null
    return fiscalYearList.reduce((minYear, current) => {
      const currentYearNum = parseInt(current.replace('年度', ''), 10)
      if (minYear === null) return current
      const minYearNum = parseInt(minYear.replace('年度', ''), 10)
      return currentYearNum < minYearNum ? current : minYear
    }, null)
  }, [fiscalYearList])

  // 前期コピーボタン活性状態
  const isCopyDisabled = selectedFiscalYear === minFiscalYear

  // 年度確定フラグ
  const isFixed = dataFixStatus === dataFixStatusConst.fix.value

  // 出向情報アップロードフラグ
  const isStdSecondmentExist = standardFormatSecondmentList.includes(selectedFiscalYear)

  /*==================================
    初期処理
  ===================================*/
  React.useEffect(() => {
    document.title = props.pageTitle
    init()
  }, [])

  const init = async () => {
    const accPeriod = props.accPeriod
    // マスター情報取得
    const pMaster = fetchMaster(props.corporationNumber)
    // データ作成タスク管理取得
    const pDataCreateTask = fetchtDataCreateTask(props.corporationNumber, props.corporationNumber)
    // 雇用区分マスタ―情報取得
    const pEmploymentType = fetchEmploymentTypesInfo(props.bucketName)
    // 雇用区分設定情報取得
    const pEmploymentTypeSetting = fetchEmploymentTypeSetting(props.bucketName)
    // 所定労働時間設定（月）取得
    const pPrescribedWorkingHoursMonth = fetchPrescribedWorkingHoursMonth(props.bucketName)
    // 所定労働時間設定（年）取得
    const pPrescribedWorkingHoursYear = fetchPrescribedWorkingHoursYear(props.bucketName)
    // 人数カウント設定情報取得
    const pEmployeeCountSetting = fetchEmployeeCountSetting(props.bucketName)
    // 出向情報取得
    const pStandardFormatSecondment = getFileNameList(
      props.bucketName,
      PathInfoList.Folder.stdSecondmentFolder,
      orderBy.asc
    )

    // 画面構成情報取得
    const [
      master,
      dataCreateTask,
      employmentTypeData,
      employmentTypeSettingData,
      prescribedWorkingHoursMonthData,
      prescribedWorkingHoursYearData,
      employeeCountSettingData,
      standardFormatSecondment
    ] = await Promise.all([
      pMaster,
      pDataCreateTask,
      pEmploymentType,
      pEmploymentTypeSetting,
      pPrescribedWorkingHoursMonth,
      pPrescribedWorkingHoursYear,
      pEmployeeCountSetting,
      pStandardFormatSecondment
    ])

    setMaster(master)

    // CSV・データ解析処理
    const employmentType = parseEmploymentTypeCsv(employmentTypeData)
    const employmentTypeSetting = parseEmploymentTypeSettingCsv(employmentTypeSettingData)
    const prescribedWorkingHoursMonth = parsePrescribedWorkingHoursMonthCsv(prescribedWorkingHoursMonthData)
    const prescribedWorkingHoursYear = parsePrescribedWorkingHoursYearCsv(prescribedWorkingHoursYearData)
    const employeeCountSetting = parseEmployeeCountSetting(employeeCountSettingData)
    const annualTask = filterDataCreateTask(dataCreateTask, accPeriod)

    // 年度リスト取得
    const fiscalYearDataList = fetchFiscalYearDateList(annualTask, accPeriod)
    // 選択年度取得
    const selectedFiscalYear = getCurrentFiscalYear(accPeriod)
    // 出向情報アップロード年度リスト取得
    const standardFormatSecondmentList = parseStandardFormatSecondment(standardFormatSecondment)

    dispatch('INIT_STATE', {
      accPeriod,
      selectedFiscalYear,
      annualTask,
      fiscalYearDataList,
      standardFormatSecondmentList,
      employmentType,
      employmentTypeSetting,
      prescribedWorkingHoursMonth,
      prescribedWorkingHoursYear,
      employeeCountSetting
    })

    setIsLoaded(true)
  }

  /*==================================
    イベントハンドラ
  ===================================*/
  // 入力エラー時にスクロールをトップへ
  React.useEffect(() => {
    if (hasErrors(fiscalYearErrors) || hasErrors(serverErrors)) {
      window.scrollTo(0, 0)
    }
  }, [JSON.stringify(fiscalYearErrors), JSON.stringify(serverErrors)])

  /**
   * 年度変更処理（年度変更アクションをdispatch）
   *
   * @param {string} fiscalYear - 選択された年度
   */
  const handleSelectChange = useCallback(
    (fiscalYear) => {
      dispatch('CHANGE_FISCAL_YEAR', { fiscalYear })
    },
    [dispatch]
  )

  /** 前年コピー処理（前年コピーアクションをdispatch） */
  const handleCopyPreviousFiscalYearData = useCallback(() => {
    dispatch('COPY_PREVIOUS_FISCAL_YEAR')
  }, [dispatch])

  /*==================================
    入力チェック・ヘルパー関数
  ===================================*/
  /**
   * チェック関数
   *
   * @returns {Promise<Object>} エラーオブジェクト { id: [errorMessage, ...], ... }
   */

  /** 入力チェック */
  const _checkInput = (data) => {
    const checkEmploymentTypeSetting = validateEmploymentTypeSetting(
      data.employmentTypeSetting,
      employmentTypeSettingFields
    )
    const checkEmployeeCountSetting = validateRequiredFields(data.employeeCountSetting, employeeCountSettingFields)
    const checkPrescribedWorkingHoursMonth = validateRequiredFields(
      data.prescribedWorkingHoursMonth,
      prescribedWorkingHoursMonthFields
    )
    const checkPrescribedWorkingHoursYear = validateRequiredFields(
      data.prescribedWorkingHoursYear,
      prescribedWorkingHoursYearFields
    )

    return mergeErrors(
      checkEmployeeCountSetting,
      checkEmploymentTypeSetting,
      checkPrescribedWorkingHoursMonth,
      checkPrescribedWorkingHoursYear
    )
  }

  /**
   * 更新前のAPI送信用データ整形
   *
   * @param {EmploymentTypeState[]} items - 現在の状態管理配列
   * @returns {Object[]} API送信用配列
   */
  function prepareFileData() {
    const {
      employeeCountSetting: employeeCountSetting = [],
      employmentTypeSetting: employmentTypeSetting = [],
      prescribedWorkingHoursMonth: prescribedWorkingHoursMonth = [],
      prescribedWorkingHoursYear: prescribedWorkingHoursYear = {}
    } = fiscalYearSettings

    const fileData = {
      updatedFiscalYear: selectedFiscalYear,
      employeeCountSetting: employeeCountSetting.map((item) => ({ ...item })),
      employmentTypeSetting: employmentTypeSetting.map((item) => ({ ...item })),
      prescribedWorkingHoursMonth: prescribedWorkingHoursMonth.map((item) => ({ ...item })),
      prescribedWorkingHoursYear: prescribedWorkingHoursYear.map((item) => ({ ...item }))
    }

    return fileData
  }
  /**
   * 更新前のAPI送信用データチェック処理
   *
   * @returns {Object[] | null} API送信用配列
   */
  const prepareCheck = () => {
    // 更新前処理
    const fileData = prepareFileData()
    // チェック処理
    const errors = _checkInput(fileData)
    if (hasErrors(errors)) {
      dispatch('SET_CLIENT_ERROR', { errors })
      return null
    }
    dispatch('CLEAR_CLIENT_ERROR')
    return fileData
  }
  /*==================================
    更新処理
  ===================================*/
  /** 更新処理 */
  const handleEmployeeCountInfoUpdate = async (fileData) => {
    try {
      // キーをスネークケースに変換
      const payload = keysToSnakeCase(fileData)
      // 雇用区分マスター更新
      const res = await updateEmployeeCount(
        props.corporationNumber,
        props.corporationName,
        props.accPeriod,
        props.userName,
        props.userId,
        payload
      )
      if (!res) throw res
      props.navigate('/master/masterList', { state: { message: 'データ反映を開始しました。' } })
    } catch (error) {
      const serverErrorMsg = getMessage('E011012')
      dispatch('SET_SERVER_ERROR', { serverErrorMsg })
      errorLog('E000001', props.userId, viewId.employeeCountInfo, 'updateEmployeeCount', error)
    }
  }

  /*==================================
    コンポーネント（JSX）
  ===================================*/
  /** @function openConfirmUpdateModal データ更新モダルを表示 */
  async function openConfirmUpdateModal(isStdSecondmentExist) {
    // チェック処理実行
    const fileData = await prepareCheck()
    if (!fileData) return

    const { promisify } = modifyClassList('.modal-window', ['w-[900px]'])

    await promisify((res) => {
      props.showModal({
        title: 'マスター設定値登録の確認',
        onClose: res,
        isWide: false,
        description: (
          <>
            <div className="text-center">設定値を登録してダッシュボードに反映します。よろしいですか？</div>
            {/* 出向情報がアップロードされていない場合、赤文字で警告表示 */}
            {!isStdSecondmentExist && (
              <div className="text-center red--text mt-5">
                ※対象決算期の出向情報がアップロードされていません。
                <br />
                出向の判定が行えないため、人数カウントが正しく行われない可能性があります。
              </div>
            )}
            <div className="mt-5 text-center">
              ※ダッシュボードへの反映には5分～10分程度かかります
              <br />
              マスター管理画面のステータスから反映状況を確認してください
            </div>
          </>
        ),
        button: [
          {
            name: '実行',
            className: 'footer-btn button_base',
            click: () => handleEmployeeCountInfoUpdate(fileData)
          }
        ]
      })
    })
  }

  /**
   * メインコンテンツ
   *
   * @returns {React.ReactElement}
   * @component
   */
  if (!isLoaded) {
    return null
  }
  return (
    <React.Fragment>
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            {/* 上階層に戻る */}
            <div className="bg main">
              <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
                <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                  <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                    <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                    <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>
                  </span>
                </Link>
              </div>
            </div>
            {/* コンテンツヘッダ */}
            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">
                {masterClassification[master.Classification].name}
              </h1>
            </header>
            {/* メインコンテンツ */}
            <div className="page-body">
              <ShowServerErrors serverErrors={serverErrors} />
              {hasErrors(fiscalYearErrors) && (
                <div>
                  <span className="red--text">入力内容に誤りがあります。</span>
                </div>
              )}
              <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-8 px-16 border-blue1 base-table-header ">
                <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                  人数カウント設定
                </h2>
                <div className="my-6">
                  有価証券報告書に記載する従業員数をカウントするための設定を行います。
                  <br />
                  この設定を行う前に、
                  <a className="text-blue-500" href="/master/dashboard/securitiesReportInfo" target="_blank">
                    有価証券報告書レポート設定
                  </a>
                  の臨時従業員の人数計算方式（計算対象および計算方法）を設定してください。
                  <br />
                  本設定を基にCHROFYを利用するグループ全社の従業員数をカウントし、有価証券報告書における従業員数の整合性を確保します。
                </div>
                {/* ボタングループ */}
                <div className="flex justify-between gap-x-5 mb-3 items-center btn-group !float-none">
                  <div className="flex items-center">
                    <div className="text-h5">対象：</div>
                    <ChrofyCustomYearSelect
                      id="chrofy-custom-select"
                      value={selectedFiscalYear}
                      onChange={handleSelectChange}
                      options={options}
                      disabled={false}
                      hasNoSelect={false}
                    />
                  </div>
                  <div className="btn-group pl-10 border-l border-blue2-lighten-20">
                    <CopyButton onClick={handleCopyPreviousFiscalYearData} disabled={isCopyDisabled} />
                  </div>
                </div>
                <div className="border-t border-blue1 my-6 -mx-16" />
                {isFixed && (
                  <div>
                    <span className="red--text">対象決算期のデータが確定しているため設定変更できません。</span>
                  </div>
                )}
                <BorderedSection>
                  {/* 小数点の扱いに関する設定 */}
                  <RoundingMethodSetting />
                  {/* 小数点の扱いに関する設定 */}
                </BorderedSection>

                <BorderedSection>
                  {/* 雇用区分に関する設定 */}
                  <EmploymentTypeSetting />
                  {/* 雇用区分に関する設定 */}
                </BorderedSection>

                <BorderedSection>
                  <div className="flex items-center justify-between mb-6">
                    FTE（実労働時間換算）の算出に使用する所定労働時間を設定します。
                  </div>
                  {/* 所定労働時間（月）の設定 */}
                  <PrescribedWorkingHoursMonthSetting accPeriod={props.accPeriod} />
                  {/* 所定労働時間（月）の設定 */}

                  {/* 所定労働時間（年）の設定 */}
                  <PrescribedWorkingHoursYearSetting />
                  {/* 所定労働時間（年）の設定 */}
                </BorderedSection>

                <BorderedSection className="">
                  <div className="flex items-center justify-between mb-6">
                    休職者、出向者を人数カウントの集計対象に含めるかどうかの設定をします。
                    <br />
                    休職者の設定は、すべての人数計算方式に適用されます。出向者の設定はヘッドカウント方式にのみ適用されます。
                  </div>
                  {/* 休職者の設定 */}
                  <EmployeeOnLeaveTargetSetting />
                  {/* 休職者の設定 */}

                  {/* 出向者の設定（単独出向） */}
                  <IndividualSecondmentSetting />
                  {/* 出向者の設定（単独出向） */}

                  {/* 出向者の設定（兼務出向） */}
                  <ConcurrentSecondmentTargetSetting />
                  {/* 出向者の設定（兼務出向） */}
                </BorderedSection>

                <div className="content-footer mt-12 mb-8">
                  <div className="border-t  border-blue1 blue1 !mt-3 -mx-8" />
                  {/*  ===== フッターボタン ===== */}
                  <div className="footer_button_area mt-8">
                    <Link
                      color="inherit"
                      variant="button"
                      underline="none"
                      component={RouterLink}
                      to="/master/masterList"
                    >
                      <button type="button" className="footer-btn button_back">
                        キャンセル
                      </button>
                    </Link>
                    <SpinnerButton
                      name="登録"
                      className="footer-btn button_base"
                      disabled={isFixed}
                      onClick={() => openConfirmUpdateModal(isStdSecondmentExist)}
                    />
                  </div>
                </div>
              </div>
              {/*  ===== 関連ダッシュボード ===== */}
              {master?.RelatedDashboard && (
                <RelatedDashboard dashboards={master?.RelatedDashboard} authority={props.authority} mode={props.mode} />
              )}
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  )
}

const BorderedSection = ({ children, className = '' }) => (
  <div className={`border border-gray-300 rounded-xl py-5 pb-8 px-8 -mx-8 mt-5 ${className}`}>{children}</div>
)

/**
 * EmployeeCountInfo コンポーネント
 *
 * Reduxの状態とReact Routerのナビゲート関数を利用して、 人数カウント設定画面の表示を制御するコンポーネント。 親会社でなく権限がない場合は画面表示を行わずForbiddenコンポーネントを返す。
 *
 * @returns {JSX.Element} 人数カウント設定画面のコンポーネント
 */
const EmployeeCountInfo = () => {
  const state = useSelector((state) => state)
  const navigate = useNavigate()
  const showModal = useModal()
  const modalExtra = useModalExtra()

  // もしくは権限を持っていない場合は画面表示しない
  if (
    !state.authority.Master_AuthInfo.Availability ||
    !state.authority.Master_AuthInfo.Auth_Conf.Dashboard.EmployeeCount_Set
  ) {
    return <Forbidden />
  }

  return (
    <EmployeeCountInfoProviderStore>
      <EmployeeCountInfoContent
        pageTitle={titleName.employeeCountInfo + titleName.common}
        corporationNumber={state.company.corporationNumber}
        corporationName={state.company.name}
        accPeriod={state.company.accPeriod}
        userId={state.user.sub}
        userName={state.user.userName}
        authority={state.authority}
        mode={state.analysis.mode}
        bucketName={'chrofy-' + process.env.REACT_APP_ENV + '-' + state.company.corporationNumber}
        navigate={navigate}
        showModal={showModal}
        modalExtra={modalExtra}
      />
    </EmployeeCountInfoProviderStore>
  )
}

export default EmployeeCountInfo
